import {
  GripVertical,
  type LucideIcon,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import {
  type Dispatch,
  type DragEvent,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { strings } from "@/I18n";
import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Switch,
} from "@/v2/ui/primitives";
import type { SidebarConfig, SidebarPreferences } from "./types";

interface SidebarCustomizeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: SidebarConfig;
  preferences: SidebarPreferences;
  onPreferencesChange: Dispatch<SetStateAction<SidebarPreferences>>;
}

type DragState = { groupId: string; itemId: string };
type DropTarget = { groupId: string; itemId: string | null };

export default function SidebarCustomizeDrawer({
  open,
  onOpenChange,
  config,
  preferences,
  onPreferencesChange,
}: SidebarCustomizeDrawerProps) {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [localOrder, setLocalOrder] = useState<Record<string, string[]>>(
    () => preferences.itemOrder
  );
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const reorderIds = (ids: string[], fromId: string, toId: string) => {
    const next = [...ids];
    const fromIndex = next.indexOf(fromId);
    const toIndex = next.indexOf(toId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return null;
    }
    next.splice(fromIndex, 1);
    next.splice(toIndex, 0, fromId);
    return next;
  };

  const moveToEnd = (ids: string[], id: string) => {
    const next = [...ids];
    const index = next.indexOf(id);
    if (index === -1 || index === next.length - 1) return null;
    next.splice(index, 1);
    next.push(id);
    return next;
  };

  const handleToggleItem = (itemId: string) => {
    setActiveItemId(itemId);
    onPreferencesChange((prev) => {
      const isHidden = prev.hiddenItems.includes(itemId);
      return {
        ...prev,
        hiddenItems: isHidden
          ? prev.hiddenItems.filter((id) => id !== itemId)
          : [...prev.hiddenItems, itemId],
      };
    });
  };

  const handleResetToDefaults = () => {
    onPreferencesChange({
      hiddenItems: [],
      itemOrder: {},
      version: 1,
    });
  };

  const handleDragStart =
    (groupId: string, itemId: string) =>
    (event: DragEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setDragState({ groupId, itemId });
      setActiveItemId(itemId);
      event.dataTransfer?.setData("text/plain", itemId);
      event.dataTransfer?.setDragImage(event.currentTarget, 0, 0);
    };

  const handleDragEnd = () => {
    setDragState(null);
    setDropTarget(null);
    setActiveItemId(null);
  };

  const handleDragOverItem =
    (groupId: string, targetId: string) =>
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (dragState?.groupId === groupId && dragState.itemId !== targetId) {
        setDropTarget({ groupId, itemId: targetId });
      }
    };

  const handleDragLeave = (groupId: string, targetId: string) => () => {
    if (dropTarget?.groupId === groupId && dropTarget.itemId === targetId) {
      setDropTarget(null);
    }
  };

  const persistItemOrder = (groupId: string, nextOrder: string[]) => {
    setLocalOrder((prev) => ({
      ...prev,
      [groupId]: nextOrder,
    }));
    onPreferencesChange((prev) => ({
      ...prev,
      itemOrder: {
        ...prev.itemOrder,
        [groupId]: nextOrder,
      },
    }));
  };

  const handleDropOnItem =
    (group: SidebarConfig[number], targetId: string) =>
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (
        !dragState ||
        dragState.groupId !== group.id ||
        dragState.itemId === targetId
      )
        return;
      const baseIds = group.items.map((item) => item.id);
      const nextOrder = reorderIds(baseIds, dragState.itemId, targetId);
      if (nextOrder) {
        persistItemOrder(group.id, nextOrder);
      }
      setDropTarget(null);
      const draggedItemId = dragState.itemId;
      setDragState(null);
      setActiveItemId(draggedItemId);
    };

  const handleDragOverEnd =
    (groupId: string) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (dragState?.groupId === groupId) {
        setDropTarget({ groupId, itemId: null });
      }
    };

  const handleDropAtEnd =
    (group: SidebarConfig[number]) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!dragState || dragState.groupId !== group.id) return;
      const baseIds = group.items.map((item) => item.id);
      const nextOrder = moveToEnd(baseIds, dragState.itemId);
      if (nextOrder) {
        persistItemOrder(group.id, nextOrder);
      }
      setDropTarget(null);
      const draggedItemId = dragState.itemId;
      setDragState(null);
      setActiveItemId(draggedItemId);
    };

  useEffect(() => {
    setLocalOrder(preferences.itemOrder);
  }, [preferences.itemOrder]);

  const orderedGroups = useMemo(() => {
    const orderGroupItems = (group: SidebarConfig[number]) => {
      const ids = localOrder[group.id] || [];
      if (ids.length === 0) {
        return group.items;
      }
      const map = new Map(group.items.map((item) => [item.id, item]));
      const ordered: typeof group.items = [];
      ids.forEach((id) => {
        const item = map.get(id);
        if (item) {
          ordered.push(item);
          map.delete(id);
        }
      });
      map.forEach((item) => ordered.push(item));
      return ordered;
    };

    return config.map((group) => ({
      ...group,
      items: orderGroupItems(group),
    }));
  }, [config, localOrder]);

  // Count visible items
  const totalItems = orderedGroups.reduce(
    (acc, group) => acc + group.items.length,
    0
  );
  const hiddenCount = preferences.hiddenItems.length;
  const visibleCount = totalItems - hiddenCount;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="tw-w-[380px] sm:tw-w-[380px] tw-flex tw-flex-col tw-p-0 tw-gap-0 tw-border-r tw-border-border/50 tw-bg-gradient-to-b tw-from-background tw-to-muted/20"
      >
        {/* Compact Header */}
        <div className="tw-px-5 tw-py-3 tw-border-b tw-border-border/50">
          <div className="tw-flex tw-items-center tw-justify-between">
            <SheetHeader className="tw-space-y-0">
              <SheetTitle className="tw-text-base tw-font-semibold tw-tracking-tight">
                {strings("sidebar.customizeMenu")}
              </SheetTitle>
            </SheetHeader>
            <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-primary/10 tw-text-primary tw-text-xs tw-font-medium">
              <Sparkles className="tw-h-3 tw-w-3" />
              {visibleCount}/{totalItems}
            </div>
          </div>
        </div>

        {/* Scrollable content - takes remaining height */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4">
          <div className="tw-flex tw-flex-col tw-gap-4">
            {orderedGroups.map((group) => {
              const GroupIcon = group.icon as LucideIcon | undefined;
              const isEndDropActive =
                dropTarget?.groupId === group.id && dropTarget?.itemId === null;

              return (
                <div key={group.id} className="tw-space-y-2">
                  {/* Group header */}
                  <div className="tw-flex tw-items-center tw-gap-2 tw-py-1">
                    {GroupIcon && (
                      <div className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded tw-bg-muted">
                        <GroupIcon className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
                      </div>
                    )}
                    <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground/70">
                      {group.name}
                    </span>
                  </div>

                  <div
                    className="tw-rounded-xl tw-border tw-border-border/60 tw-bg-card/50 tw-backdrop-blur-sm tw-overflow-hidden tw-divide-y tw-divide-border/40"
                    role="list"
                  >
                    {group.items.map((item) => {
                      const ItemIcon = item.icon as LucideIcon | undefined;
                      const isHidden = preferences.hiddenItems.includes(
                        item.id
                      );
                      const isDropHover =
                        dropTarget?.groupId === group.id &&
                        dropTarget.itemId === item.id;
                      const isDragging = dragState?.itemId === item.id;
                      const isActive = activeItemId === item.id;

                      const rowClasses = [
                        "tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-transition-all tw-duration-200",
                        isHidden ? "tw-opacity-50" : "",
                        isDropHover
                          ? "tw-border tw-border-primary/40 tw-bg-primary/5 tw-shadow-lg"
                          : "",
                        isDragging ? "tw-bg-muted/20 tw-shadow-inner" : "",
                        isActive
                          ? "tw-ring-2 tw-ring-offset-2 tw-ring-primary/60"
                          : "",
                      ];

                      return (
                        <div
                          key={item.id}
                          className={rowClasses.filter(Boolean).join(" ")}
                          tabIndex={0}
                          onMouseDown={() => setActiveItemId(item.id)}
                          aria-selected={isActive}
                          onDragOver={handleDragOverItem(group.id, item.id)}
                          onDragLeave={handleDragLeave(group.id, item.id)}
                          onDrop={handleDropOnItem(group, item.id)}
                        >
                          <button
                            type="button"
                            draggable={true}
                            onDragStart={handleDragStart(group.id, item.id)}
                            onDragEnd={handleDragEnd}
                            className="tw-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded tw-text-muted-foreground hover:tw-bg-muted/20 tw-cursor-grab"
                            aria-label={`Reorder ${item.name}`}
                          >
                            <GripVertical className="tw-h-4 tw-w-4" />
                          </button>

                          {ItemIcon && (
                            <div
                              className={`
                                tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg
                                tw-transition-colors tw-duration-200
                                ${
                                  isHidden
                                    ? "tw-bg-muted/50 tw-text-muted-foreground/50"
                                    : "tw-bg-primary/10 tw-text-primary"
                                }
                              `}
                            >
                              <ItemIcon className="tw-h-4 tw-w-4" />
                            </div>
                          )}

                          {/* Label and badge */}
                          <div className="tw-flex-1 tw-min-w-0">
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <span
                                className={`
                                  tw-text-sm tw-font-medium tw-truncate
                                  tw-transition-colors tw-duration-200
                                  ${isHidden ? "tw-text-muted-foreground/70" : "tw-text-foreground"}
                                `}
                              >
                                {item.name}
                              </span>
                              {item.badge && (
                                <span
                                  className="tw-px-1.5 tw-py-0.5 tw-text-[9px] tw-font-semibold tw-rounded tw-text-white tw-uppercase tw-tracking-wide"
                                  style={{
                                    backgroundColor:
                                      item.badgeColor || "#3b82f6",
                                  }}
                                >
                                  {item.badge}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Toggle switch */}
                          <Switch
                            checked={!isHidden}
                            onCheckedChange={() => handleToggleItem(item.id)}
                            className="tw-data-[state=checked]:tw-bg-primary"
                          />
                        </div>
                      );
                    })}
                    <div
                      className={`
                        tw-h-4
                        tw-mx-4
                        tw-rounded
                        tw-border-t
                        tw-border-dashed
                        ${isEndDropActive ? "tw-border-primary" : "tw-border-border/40"}
                      `}
                      onDragOver={handleDragOverEnd(group.id)}
                      onDrop={handleDropAtEnd(group)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Compact Footer */}
        <div className="tw-px-5 tw-pt-2.5 tw-pb-2 tw-border-t tw-border-border/50 tw-bg-muted/20">
          <Button
            onClick={handleResetToDefaults}
            variant="ghost"
            size="sm"
            className="tw-w-full tw-h-8 tw-text-xs tw-text-muted-foreground"
          >
            <RotateCcw className="tw-h-3.5 tw-w-3.5 tw-mr-1.5" />
            {strings("sidebar.resetToDefaults")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
